import React, { useMemo, useState } from 'react';
import type { Sucursal } from '../types';
import { PageShell, glassLight, primaryButton, ghostButton, Badge, EmptyState, MotionGrid, MotionItem, useIsMobile, usePagination, Pagination, T, Modal } from '../ui';

// Iconos + acento por tipo de sucursal, para que cada card tenga identidad visual propia.
const TIPO_STYLES: Record<string, {
  icon: JSX.Element;
  color: string;
  soft: string;
}> = {
  tienda: {
    color: '#FF6B01',
    soft: 'rgba(255,107,1,.12)',
    icon: <path d="M3 9l1-5h16l1 5M4 9h16v10a1 1 0 0 1-1 1H5a1 1 0 0 1-1-1V9zM9 21v-6h6v6" data-id="element-952" data-mp="1v3htptb2xop" />
  },
  bodega: {
    color: '#2563eb',
    soft: 'rgba(37,99,235,.12)',
    icon: <path d="M3 21h18M5 21V7l8-4v18M19 21V11l-6-4M9 9v.01M9 12v.01M9 15v.01M9 18v.01" data-id="element-953" data-mp="3k6qk8en8xf07" />
  },
  oficina: {
    color: '#7c3aed',
    soft: 'rgba(124,58,237,.12)',
    icon: <path d="M4 21V7l8-4 8 4v14M9 21v-6h6v6M9 12h.01M15 12h.01M9 9h.01M15 9h.01" data-id="element-954" data-mp="gq9q6jfvj8l7" />
  },
  default: {
    color: '#16a34a',
    soft: 'rgba(22,163,74,.12)',
    icon: <path d="M3 21h18M5 21V7l8-4v18M19 21V11l-6-4M9 9v.01M9 12v.01M9 15v.01M9 18v.01" data-id="element-955" data-mp="2o9yxx7lq1171" />
  }
};
function tipoStyle(tipo: string) {
  const key = tipo?.toLowerCase().trim();
  return TIPO_STYLES[key] || TIPO_STYLES.default;
}
export function Sucursales({
  sucs,
  onNew,
  onEdit,
  onDelete,
  canArchive
}: {
  sucs: Sucursal[];
  onNew?: () => void;
  onEdit?: (id: string) => void;
  onDelete?: (id: string) => void;
  canArchive?: boolean;
}) {
  const [tab, setTab] = useState<'activos' | 'archivados'>('activos');
  const [confirm, setConfirm] = useState<string | null>(null);
  const isMobile = useIsMobile();
  const filteredSucs = useMemo(() => {
    return sucs.filter(s => tab === 'activos' ? !s.archivado : s.archivado);
  }, [sucs, tab]);
  const {
    page,
    setPage,
    totalPages,
    pageItems,
    rangeStart,
    rangeEnd,
    total
  } = usePagination(filteredSucs, 6);
  const activosCount = useMemo(() => sucs.filter(s => !s.archivado).length, [sucs]);
  const archivadosCount = useMemo(() => sucs.filter(s => s.archivado).length, [sucs]);
  const tiposCount = useMemo(() => new Set(sucs.filter(s => !s.archivado).map(s => s.tipo)).size, [sucs]);
  return <PageShell title="Sucursales" sub={`${filteredSucs.length} puntos comerciales`} action={onNew ? <button onClick={onNew} style={primaryButton} data-id="element-957" data-mp="3rotp4rfj4uos">
            + Nueva sucursal
          </button> : undefined} data-id="element-956" data-mp="3rmh0jvf9nm2r">

      <style data-id="element-958" data-mp="4i13xcnhhetf">{`
        .suc-card {
          position: relative;
          overflow: hidden;
          transition: transform .2s cubic-bezier(.2,.8,.2,1), box-shadow .2s ease, border-color .2s ease;
        }
        .suc-card::before {
          content: '';
          position: absolute;
          inset: 0 0 auto 0;
          height: 3px;
          background: var(--suc-accent, ${T.accent});
          opacity: .9;
        }
        @media (min-width: 768px) {
          .suc-card:hover {
            transform: translateY(-4px);
            box-shadow: 0 18px 44px rgba(2,6,23,.14);
            border-color: var(--suc-accent, ${T.accent});
          }
        }
        .suc-icon-wrap {
          transition: transform .25s ease;
        }
        .suc-card:hover .suc-icon-wrap {
          transform: scale(1.08) rotate(-4deg);
        }
        .suc-stat {
          display: flex;
          align-items: center;
          gap: 10px;
          padding: 14px 16px;
          border-radius: 14px;
        }
      `}</style>

      {/* ── Resumen rápido ─────────────────────────────────────────── */}
      <div style={{
      display: 'grid',
      gridTemplateColumns: isMobile ? '1fr 1fr' : 'repeat(3, minmax(160px, 1fr))',
      gap: 12,
      marginBottom: 22
    }} data-id="element-959" data-mp="2rdr3tixzywoc">
        <div className="suc-stat" style={{
        ...glassLight,
        borderLeft: `3px solid ${T.accent}`
      }} data-id="element-960" data-mp="2ml7s6rivebm6">
          <div style={{
          fontSize: 22,
          fontWeight: 800,
          color: T.text
        }} data-id="element-961" data-mp="3f4dap6w9mawr">{activosCount}</div>
          <div style={{
          fontSize: 12,
          color: T.textMuted,
          fontWeight: 600
        }} data-id="element-962" data-mp="3acd3quhs2hqo">Activas</div>
        </div>
        <div className="suc-stat" style={{
        ...glassLight,
        borderLeft: `3px solid ${T.info}`
      }} data-id="element-963" data-mp="2tzyuvzvudemi">
          <div style={{
          fontSize: 22,
          fontWeight: 800,
          color: T.text
        }} data-id="element-964" data-mp="2kx7edlo3zso0">{tiposCount}</div>
          <div style={{
          fontSize: 12,
          color: T.textMuted,
          fontWeight: 600
        }} data-id="element-965" data-mp="2px277urg2sbp">Tipos distintos</div>
        </div>
        {!isMobile && <div className="suc-stat" style={{
        ...glassLight,
        borderLeft: `3px solid ${T.textMuted}`
      }} data-id="element-966" data-mp="3hn6x28xz47kb">
            <div style={{
          fontSize: 22,
          fontWeight: 800,
          color: T.text
        }} data-id="element-967" data-mp="33wleklcflzmy">{archivadosCount}</div>
            <div style={{
          fontSize: 12,
          color: T.textMuted,
          fontWeight: 600
        }} data-id="element-968" data-mp="38tbpku9dq51i">Archivadas</div>
          </div>}
      </div>

      <div style={{
      display: 'flex',
      gap: 8,
      marginBottom: 20
    }} data-id="element-969" data-mp="56bdf26ltzw0">
        <button onClick={() => {
        setTab('activos');
        setPage(1);
      }} style={{
        ...ghostButton,
        background: tab === 'activos' ? T.accentSoft : 'transparent',
        color: tab === 'activos' ? T.accent : T.textMuted,
        borderColor: tab === 'activos' ? T.accentBorder : 'rgba(0,0,0,0.08)'
      }} data-id="element-970" data-mp="ekd6zqodh6eh">
          Activos
        </button>
        {canArchive && <button onClick={() => {
        setTab('archivados');
        setPage(1);
      }} style={{
        ...ghostButton,
        background: tab === 'archivados' ? T.accentSoft : 'transparent',
        color: tab === 'archivados' ? T.accent : T.textMuted,
        borderColor: tab === 'archivados' ? T.accentBorder : 'rgba(0,0,0,0.08)'
      }} data-id="element-971" data-mp="3rr55ran95q1f">
            Archivados
          </button>}
      </div>

      {sucs.length === 0 ? <EmptyState title="Aún no hay sucursales" hint="Crea tu primer punto comercial para empezar a registrar consumo." data-id="element-972" data-mp="1gyardm2duepn" /> : <MotionGrid style={{
      display: 'grid',
      gridTemplateColumns: isMobile ? '1fr' : 'repeat(auto-fill,minmax(340px,1fr))',
      gap: 16
    }} data-id="element-973" data-mp="1bz7ni4y52ej9">
          {pageItems.map(s => {
        const style = tipoStyle(s.tipo);
        return <MotionItem key={s.id} className="getr-card suc-card" style={{
          ...glassLight,
          padding: isMobile ? 20 : 22,
          paddingTop: isMobile ? 24 : 26,
          display: 'flex',
          flexDirection: 'column',
          ['--suc-accent' as any]: style.color
        }} data-id="element-974" data-mp="1o44vm9h3cicb">

                <div style={{
            display: 'flex',
            flexDirection: isMobile ? 'column' : 'row',
            justifyContent: 'space-between',
            gap: 12,
            flex: 1
          }} data-id="element-975" data-mp="15bsjiv0my2oz">

                  <div style={{
              display: 'flex',
              gap: 13,
              minWidth: 0
            }} data-id="element-976" data-mp="1f1c6abrgygp4">

                    <div className="suc-icon-wrap" style={{
                width: 46,
                height: 46,
                borderRadius: 14,
                flexShrink: 0,
                background: style.soft,
                border: `1px solid ${style.color}40`,
                color: style.color,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }} data-id="element-977" data-mp="hl6ktuciturn">

                      <svg width="21" height="21" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" data-id="element-978" data-mp="55fbrfnjknx4">
                        {style.icon}
                      </svg>
                    </div>
                    <div style={{
                minWidth: 0
              }} data-id="element-979" data-mp="3ngtofc09g5g0">
                      <div style={{
                  fontWeight: 700,
                  color: T.text,
                  fontSize: 15.5,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap'
                }} data-id="element-980" data-mp="3ihphyt4q0ltd">
                        {s.nombre}
                      </div>
                      <div style={{
                  marginTop: 6
                }} data-id="element-981" data-mp="34jj0gnk2wyfv">
                        <Badge label={s.tipo} color={style.color} soft={style.soft} dot={false} data-id="element-982" data-mp="wakn9akytvr7" />
                      </div>
                      <div style={{
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: 6,
                  fontSize: 12,
                  color: T.textMuted,
                  marginTop: 10,
                  lineHeight: 1.4
                }} data-id="element-983" data-mp="116rx3eghfref">
                        <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{
                    flexShrink: 0,
                    marginTop: 2,
                    opacity: 0.7
                  }} data-id="element-984" data-mp="dhp4xco82g5o">
                          <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" data-id="element-985" data-mp="8i45l15545sf" />
                          <circle cx="12" cy="10" r="3" data-id="element-986" data-mp="3qqv6jro0xgaq" />
                        </svg>
                        <span data-id="element-987" data-mp="3ur5gtgiumsve">{s.direccion}</span>
                      </div>
                    </div>
                  </div>
                  {(onEdit || onDelete) && <div style={{
              display: 'flex',
              gap: 8,
              flexShrink: 0,
              width: isMobile ? '100%' : 'auto',
              marginTop: isMobile ? 4 : 0,
              paddingTop: isMobile ? 12 : 0,
              borderTop: isMobile ? `1px solid ${T.border}` : 'none'
            }} data-id="element-988" data-mp="374jpx5atp2q9">

                    {onEdit && <button className="getr-btn-ghost" onClick={() => onEdit(s.id)} style={{
                ...ghostButton,
                flex: isMobile ? 1 : 'none'
              }} data-id="element-989" data-mp="3v0nx82sz2uyh">
                        Editar
                      </button>}
                    {onDelete && <button onClick={() => setConfirm(s.id)} style={{
                ...ghostButton,
                flex: isMobile ? 1 : 'none',
                color: s.archivado ? T.info : T.warn,
                borderColor: s.archivado ? 'rgba(96,165,250,0.3)' : 'rgba(251,191,36,0.3)',
                background: s.archivado ? 'rgba(96,165,250,0.1)' : 'rgba(251,191,36,0.1)'
              }} data-id="element-990" data-mp="3nb6ih0g5utf">
                        {s.archivado ? 'Desarchivar' : 'Archivar'}
                      </button>}
                  </div>}
                </div>
              </MotionItem>;
      })}
        </MotionGrid>}
      {sucs.length > 0 && <Pagination page={page} totalPages={totalPages} onChange={setPage} rangeStart={rangeStart} rangeEnd={rangeEnd} total={total} label="sucursales" data-id="element-991" data-mp="2n56xyceqc6n9" />}

      {confirm && <Modal title="Confirmar acción" onClose={() => setConfirm(null)} data-id="element-992" data-mp="3fsug7ksj2l6q">
          <div style={{
        color: T.textSecondary,
        marginBottom: 24,
        fontSize: 14,
        lineHeight: 1.5
      }} data-id="element-993" data-mp="3fqqd352gdq7i">
            ¿Estás seguro de que deseas{' '}
            {sucs.find(s => s.id === confirm)?.archivado ? 'desarchivar' : 'archivar'}{' '}
            este punto comercial?
          </div>
          <div style={{
        display: 'flex',
        gap: 12,
        justifyContent: 'flex-end'
      }} data-id="element-994" data-mp="1ufhy42dracn8">
            <button onClick={() => setConfirm(null)} style={{
          ...ghostButton,
          padding: '10px 20px'
        }} data-id="element-995" data-mp="1kc2tbdhali15">
              Cancelar
            </button>
            <button onClick={() => {
          if (onDelete) onDelete(confirm);
          setConfirm(null);
        }} style={{
          ...primaryButton,
          background: sucs.find(s => s.id === confirm)?.archivado ? T.info : T.warn,
          borderColor: sucs.find(s => s.id === confirm)?.archivado ? T.info : T.warn,
          color: '#0B1120'
        }} data-id="element-996" data-mp="1u9h5v203hmo6">
              Sí,{' '}
              {sucs.find(s => s.id === confirm)?.archivado ? 'desarchivar' : 'archivar'}
            </button>
          </div>
        </Modal>}
    </PageShell>;
}